/**
 * @copyright (C) 2023, RSupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of RSupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by RSupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jun 22 2023
 **/

import React from "react";
import styled from "@emotion/styled";

import { Divider, Menu } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { SceneType } from "@/frameworks/libraries/commons";
import { Maybe } from "@/frameworks/libraries/components";
import { Properties } from "@/frameworks/libraries/properties";

import { POPOVER_ID } from "@/frameworks/core/commons";
import { usePopover } from "@/frameworks/core/hooks";
import { usePersistUser } from "@/frameworks/core/persistence";

interface ChatSubMeuProps {
	onClickItem?: (ev: React.MouseEvent<HTMLLIElement>) => void;
	isMe: boolean;
	type?: "image" | "section" | "vote";
	variant: ChatUI.Size;
	spaceID?: string;
	popoverID?: POPOVER_ID;
	threadID?: string;
	hideAnnounceMenu?: boolean;
}

export default function ChatSubMenu({
	onClickItem,
	isMe,
	type = "section",
	variant,
	spaceID,
	popoverID,
	threadID,
	hideAnnounceMenu
}: ChatSubMeuProps) {
	const { hide } = usePopover();
	const me = usePersistUser("me");

	const handleClickItem = (ev: React.MouseEvent<HTMLLIElement>) => {
		onClickItem?.(ev);

		hide(popoverID || POPOVER_ID.CHAT_SUB_MENU);
	};

	const isOpenWorld =
		variant === "transparent" &&
		(me.currentSpace?.spaceScene.sceneType?.typeNumber || 0) === SceneType.OpenWorldScene;

	const isMyRoomSpace =
		variant === "transparent" && (me.currentSpace?.spaceScene.sceneType?.typeNumber || 0) === SceneType.MyRoomScene;

	const isOtherMyRoom = isMyRoomSpace && spaceID !== Properties.property.user?.myRoomId;

	if (type === "vote") {
		return (
			<StyledMenuList type={type}>
				<Menu.Item id="reply" onClick={handleClickItem} typoSize="Body3_MD">
					{_T("답장하기")}
				</Menu.Item>
				<Maybe test={isMe}>
					<Divider type="line" />
					<Menu.Item id="delete" onClick={handleClickItem} typoSize="Body3_MD" typoColor="warning_80_100">
						{_T("삭제하기")}
					</Menu.Item>
				</Maybe>
			</StyledMenuList>
		);
	}

	if (type === "image") {
		return (
			<StyledMenuList type={type}>
				<StyledMenuItem id="copy" onClick={handleClickItem} typoSize="Body3_MD">
					{_T("이미지 복사")}
				</StyledMenuItem>
				<Maybe test={isMe}>
					<Divider type="line" />
					<StyledMenuItem
						id="delete"
						onClick={handleClickItem}
						typoSize="Body3_MD"
						typoColor="warning_80_100"
					>
						{_T("이미지 삭제")}
					</StyledMenuItem>
				</Maybe>
			</StyledMenuList>
		);
	}

	return (
		<StyledMenuList type={type}>
			<Maybe test={isMe}>
				<Menu.Item id="update" onClick={handleClickItem} typoSize="Body3_MD">
					{_T("수정하기")}
				</Menu.Item>
			</Maybe>
			{!threadID && (
				<Menu.Item id="thread" onClick={handleClickItem} typoSize="Body3_MD">
					{_T("스레드 남기기")}
				</Menu.Item>
			)}
			<Menu.Item id="reply" onClick={handleClickItem} typoSize="Body3_MD">
				{_T("답장하기")}
			</Menu.Item>
			<Menu.Item id="copy" onClick={handleClickItem} typoSize="Body3_MD">
				{_T("텍스트 복사")}
			</Menu.Item>
			<Maybe test={!isOpenWorld && !isOtherMyRoom && !hideAnnounceMenu}>
				<Menu.Item id="notice" onClick={handleClickItem} typoSize="Body3_MD">
					{_T("공지하기")}
				</Menu.Item>
			</Maybe>
			<Maybe test={isMe}>
				<Divider type="line" />
				<Menu.Item id="delete" onClick={handleClickItem} typoSize="Body3_MD" typoColor="warning_80_100">
					{_T("삭제하기")}
				</Menu.Item>
			</Maybe>
		</StyledMenuList>
	);
}

const StyledMenuList = styled(Menu.List)<{ type: ChatSubMeuProps["type"] }>`
	min-width: 81px;
	width: fit-content;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	padding: 8px;
	border-radius: 8px;
	box-shadow:
		0px 8px 16px 0px rgba(0, 0, 0, 0.2),
		0px 0px 4px 0px rgba(0, 0, 0, 0.04);
`;

const StyledMenuItem = styled(Menu.Item)`
	.buttonText {
		white-space: nowrap;
	}
`;
